import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Chip } from "../../components/Common";
import { Pill, Plus, ShieldCheck, X } from "lucide-react";

export const WARDS = ["All", "General", "ICU", "Paediatric", "Maternity", "Isolation", "Surgical"];
export const WARDS_NO_ALL = WARDS.slice(1);
export const STATUSES = ["Stable", "Under observation", "Critical", "Improving"];
export const DISCHARGE_CONDITIONS = ["Improved", "Stable", "Deteriorated", "Deceased"];

export function StatusChip({ value }) {
  const tone =
    value === "Critical" ? "red" :
    value === "Under observation" ? "amber" :
    value === "Improving" ? "blue" :
    value === "Stable" ? "green" : "slate";
  return <Chip tone={tone}>{value || "Not set"}</Chip>;
}

export function AddMedicationDialog({ admissionId, onClose, onSaved }) {
  const [drug, setDrug] = useState("");
  const [dose, setDose] = useState("");
  const [route, setRoute] = useState("PO");
  const [frequency, setFrequency] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!drug.trim() || !dose.trim() || !frequency.trim()) { toast.error("Drug, dose and frequency are required"); return; }
    try {
      setSaving(true);
      await api.post(`/admissions/${admissionId}/medications`, { drug: drug.trim(), dose: dose.trim(), route, frequency: frequency.trim(), notes: notes.trim() || undefined });
      toast.success("Medication added");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add medication"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200" data-testid="add-med-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><Pill className="w-4 h-4 text-[#1976D2]" /> Add inpatient medication</div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs text-slate-500 font-medium">Drug *</label>
            <input data-testid="med-drug" value={drug} onChange={(e) => setDrug(e.target.value)} placeholder="e.g. Ceftriaxone 1g" className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 font-medium">Dose *</label>
              <input data-testid="med-dose" value={dose} onChange={(e) => setDose(e.target.value)} placeholder="e.g. 1 g" className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium">Route</label>
              <select data-testid="med-route" value={route} onChange={(e) => setRoute(e.target.value)} className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                {["PO","IV","IM","SC","PR","Topical","Inhaled"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Frequency *</label>
            <input data-testid="med-frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)} placeholder="e.g. Every 12 hours" className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Notes</label>
            <textarea data-testid="med-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Optional" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
          <button data-testid="med-submit" disabled={saving} type="submit" className="h-9 px-4 rounded-lg bg-[#1976D2] text-white text-sm font-medium disabled:opacity-60">{saving ? "Saving…" : "Add medication"}</button>
        </div>
      </form>
    </div>
  );
}

export function WardRoundEditor({ admissionId, onSaved, onCancel }) {
  const [subjective, setSubjective] = useState("");
  const [objective, setObjective] = useState("");
  const [assessment, setAssessment] = useState("");
  const [plan, setPlan] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (sign) => {
    if (!(subjective || objective || assessment || plan).trim()) { toast.error("Note cannot be empty"); return; }
    try {
      setSaving(true);
      await api.post(`/admissions/${admissionId}/ward-rounds`, {
        subjective: subjective || null, objective: objective || null,
        assessment: assessment || null, plan: plan || null, sign,
      });
      toast.success(sign ? "Ward round signed" : "Ward round saved");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save ward round"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="ward-round-editor">
      <div className="font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4 text-[#1976D2]" /> New ward round note</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 font-medium">Subjective</label>
          <textarea data-testid="wr-subjective" value={subjective} onChange={(e) => setSubjective(e.target.value)} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="How is the patient today?" />
        </div>
        <div>
          <label className="text-xs text-slate-500 font-medium">Objective</label>
          <textarea data-testid="wr-objective" value={objective} onChange={(e) => setObjective(e.target.value)} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Exam findings, vitals summary" />
        </div>
        <div>
          <label className="text-xs text-slate-500 font-medium">Assessment</label>
          <textarea data-testid="wr-assessment" value={assessment} onChange={(e) => setAssessment(e.target.value)} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Impression / differentials" />
        </div>
        <div>
          <label className="text-xs text-slate-500 font-medium">Plan</label>
          <textarea data-testid="wr-plan" value={plan} onChange={(e) => setPlan(e.target.value)} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Investigations, meds, follow-up" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button data-testid="wr-cancel" type="button" onClick={onCancel} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
        <button data-testid="wr-save-draft" type="button" disabled={saving} onClick={() => submit(false)} className="h-9 px-3 rounded-lg text-sm border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60">Save draft</button>
        <button data-testid="wr-sign" type="button" disabled={saving} onClick={() => submit(true)} className="h-9 px-4 rounded-lg text-sm font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] disabled:opacity-60 flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4" /> Sign &amp; lock
        </button>
      </div>
    </div>
  );
}

export function AddendumBox({ admissionId, roundId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!text.trim()) { toast.error("Addendum cannot be empty"); return; }
    try {
      setSaving(true);
      await api.post(`/admissions/${admissionId}/ward-rounds/${roundId}/addendum`, { text: text.trim() });
      toast.success("Addendum appended");
      setText(""); setOpen(false); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add addendum"); }
    finally { setSaving(false); }
  };

  if (!open) return (
    <button data-testid={`addendum-open-${roundId}`} onClick={() => setOpen(true)} className="text-xs text-[#1565C0] font-medium hover:underline">+ Add addendum</button>
  );
  return (
    <div className="mt-2 space-y-2" data-testid={`addendum-editor-${roundId}`}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Additional notes to append (does not modify the signed note)…" data-testid={`addendum-text-${roundId}`} />
      <div className="flex items-center justify-end gap-2">
        <button onClick={() => { setOpen(false); setText(""); }} className="h-8 px-3 rounded-lg text-xs text-slate-600 hover:bg-slate-100">Cancel</button>
        <button data-testid={`addendum-save-${roundId}`} disabled={saving} onClick={submit} className="h-8 px-3 rounded-lg text-xs font-medium bg-[#1976D2] text-white disabled:opacity-60">{saving ? "Saving…" : "Append"}</button>
      </div>
    </div>
  );
}
